import Add from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import {
  DateRangePicker,
  LocalizationProvider,
  MultiInputDateRangeField,
  PickersShortcutsProps,
} from "@mui/x-date-pickers-pro";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { CellText } from "components/Omnisearch/helpComponents/CellText";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { SubmissionReviewerCell } from "components/Submission/SubmissionReviewerCell";
import {
  SignatureType,
  SubmissionStatus,
  SubmissionVirtualEventsQuery,
} from "generated-graphql/graphql";
import { useAutoFlex } from "hooks/useAutoFlex";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import {
  OmnisearchGridColDef,
  useOmnisearchDatagrid,
} from "hooks/useOmnisearchDatagrid";
import { DateTime } from "luxon";
import { SUBMISSION_VIRTUAL_EVENTS_QUERY } from "queries/submissions";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SubmissionBulkActionsMenu } from "./SubmissionBulkActionsMenu";
import { SubmissionsEmptyState } from "./SubmissionsEmptyState";

export type Row =
  SubmissionVirtualEventsQuery["submissionVirtualEvents"]["items"][0];

export const STATUS_LABELS: Record<string, string> = {
  [SubmissionStatus.NotStarted]: "🔶 Upcoming",
  [SubmissionStatus.InProgress]: "🚧 In Progress",
  [SubmissionStatus.EncampInitialReview]: "⛺️ In Encamp Review",
  [SubmissionStatus.InReview]: "🔍 In Review",
  [SubmissionStatus.ProcessingSubmission]: "✅ Awaiting Submission",
  [SubmissionStatus.FilingComplete]: "🎉 Submitted",
  [SubmissionStatus.NotReporting]: "⭕️ Not Reporting",
};

// Status filter options for multi-select checkbox menu
const STATUS_FILTER_OPTIONS: Record<SubmissionStatus, string> = {
  [SubmissionStatus.NotStarted]: "🔶 Upcoming",
  [SubmissionStatus.InProgress]: "🚧 In Progress",
  [SubmissionStatus.EncampInitialReview]: "⛺️ In Encamp Review",
  [SubmissionStatus.InReview]: "🔍 In Review",
  [SubmissionStatus.ProcessingSubmission]: "✅ Awaiting Submission",
  [SubmissionStatus.FilingComplete]: "🎉 Submitted",
  [SubmissionStatus.NotReporting]: "⭕️ Not Reporting",
};

// US States list for filtering
const US_STATES = Object.keys(STATE_NAMES);

// Date picker shortcuts
const datePickerShortcuts: PickersShortcutsProps<DateRange<DateTime>>["items"] =
  [
    {
      label: "This Year",
      getValue: () => [
        DateTime.now().startOf("year"),
        DateTime.now().endOf("year"),
      ],
    },
    {
      label: "Last Year",
      getValue: () => [
        DateTime.now().minus({ years: 1 }).startOf("year"),
        DateTime.now().minus({ years: 1 }).endOf("year"),
      ],
    },
    {
      label: "Reset",
      getValue: () => [null, null],
    },
  ];

// Default = In Progress + Upcoming
const DEFAULT_STATUSES = [
  SubmissionStatus.NotStarted,
  SubmissionStatus.InProgress,
];

const ALL_STATUSES = Object.keys(STATUS_FILTER_OPTIONS) as SubmissionStatus[];

// Default date filter = current year
const DEFAULT_DUE_DATE_START = DateTime.now().startOf("year").toISODate();
const DEFAULT_DUE_DATE_END = DateTime.now().endOf("year").toISODate();
const DEFAULT_DUE_DATE = `${DEFAULT_DUE_DATE_START}..${DEFAULT_DUE_DATE_END}`;

// Shared initial filters - used by both useOmnisearchDatagrid and OmnisearchDataGrid
const INITIAL_FILTERS = [
  {
    field: "submissionStatus",
    value: DEFAULT_STATUSES.join("|"),
  },
  {
    field: "dueDate",
    value: DEFAULT_DUE_DATE,
  },
];

const getStatusLabel = (status: string): string => {
  return STATUS_LABELS[status] || status;
};

// Helper to extract a key's value from an omnisearch string
function extractOmnisearchValue(
  omnisearch: string | null,
  key: string
): string | undefined {
  if (!omnisearch) return undefined;
  const regex = new RegExp(`(?:^|\\s)${key}:(?:"([^"]+)"|([^\\s]+))`, "i");
  const match = omnisearch.match(regex);
  if (match) {
    return match[1] ?? match[2];
  }
  return undefined;
}

// Note: This component is wrapped with OmnisearchProvider in main.tsx router
export function WasteSubmissions() {
  return <WasteSubmissionsContent />;
}

function WasteSubmissionsContent() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { isStaff, user } = useCurrentUser();
  const currentUserEmail = user?.email;
  const [selectedRows, setSelectedRows] = useState<Row[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const handleRowClick = useCallback(
    (row: Row) => {
      const submissionId = row.submission?.id;
      if (!isStaff) {
        navigate(`/o/${tenantId}/waste/submissions/${submissionId}`);
        return;
      }

      // Staff users: check submission status
      const isVirtualSubmission =
        row.submission?.status === SubmissionStatus.NotStarted;

      if (isVirtualSubmission) {
        // Navigate to create submission with taskId and startDate parameters
        const dateOnly = DateTime.fromISO(String(row.deadline)).toISODate();
        navigate(
          `/o/${tenantId}/waste/submissions/create?taskId=${row.taskId}&startDate=${dateOnly}`
        );
      } else {
        // Determine if submission should be viewed in read-only mode
        const status = row.submission?.status;
        const shouldViewReadOnly =
          status === SubmissionStatus.EncampInitialReview ||
          status === SubmissionStatus.InReview ||
          status === SubmissionStatus.FilingComplete ||
          status === SubmissionStatus.NotReporting;

        if (shouldViewReadOnly) {
          // Navigate to read-only view mode for:
          // - ENCAMP_INITIAL_REVIEW: Staff reviewer evaluates before sending to customers
          // - IN_REVIEW: Customers are actively reviewing/signing - staff should not be editing
          // - FILING_COMPLETE: Terminal state - nothing left to edit
          // - NOT_REPORTING: Opt-out status - rarely needs changes
          navigate(`/o/${tenantId}/waste/submissions/${submissionId}`);
        } else {
          // Navigate to edit mode for:
          // - IN_PROGRESS: Staff actively works on draft
          // - PROCESSING_SUBMISSION: Staff is filing - may need to update metadata
          navigate(`/o/${tenantId}/waste/submissions/${submissionId}/edit`);
        }
      }
    },
    [isStaff, navigate, tenantId]
  );

  const handleAddSubmission = useCallback(() => {
    navigate(`/o/${tenantId}/waste/submissions/create`);
  }, [navigate, tenantId]);

  useBreadcrumb([
    { label: "Waste", to: `/o/${tenantId}/waste` },
    { label: "Submissions" },
  ]);

  const omnisearchPersistenceKey = "submissions-list-view-v9";
  const autoFlex = useAutoFlex(
    1.0,
    {
      deadline: 0.1,
      title: 0.25,
      facilities: 0.1,
      "submission.status": 0.1,
      submissionType: 0.1,
      "submission.title": 0.1,
    },
    [
      "facilities",
      "submissionType",
      "title",
      "documents",
      "reviewers",
      "signer",
      "staffAssignee",
      "submission.status",
      "submission.title",
    ]
  );

  const columns: OmnisearchGridColDef<Row>[] = [
    {
      field: "deadline",
      headerName: "Due Date",
      sortable: true,
      flex: autoFlex.get("deadline"),
      minWidth: 20,
      renderCell: ({ value }) => {
        return (
          <CellText variant="body2">
            {DateTime.fromISO(value).toFormat("MM/dd/yyyy")}
          </CellText>
        );
      },
    },
    {
      field: "facilities",
      headerName: "Facilities",
      flex: autoFlex.get("facilities"),
      minWidth: 20,
      sortable: true,
      renderCell({ row }) {
        return (
          <Stack
            spacing={0}
            style={{
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
              height: "100%",
              width: "100%",
              alignItems: "start",
              overflow: "hidden",
            }}
          >
            {(row.facilities ?? []).map((facility) => (
              <Typography
                key={facility.id}
                variant="body2"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                <Tooltip title={facility.name}>
                  <span>{facility.name}</span>
                </Tooltip>
              </Typography>
            ))}
          </Stack>
        );
      },
    },
    {
      field: "title",
      headerName: "Submission Type",
      flex: autoFlex.get("title"),
      minWidth: 100,
      sortable: true,
      renderCell: ({ row: { title } }) => {
        return (
          <CellText variant="body2" noWrap>
            {/* For SYSTEM submissions, this is the pretty printed submission type
            For user submissions, this is the task title which is meant to be in sync with the submission title*/}
            {title}
          </CellText>
        );
      },
    },
    {
      field: "submission.title",
      headerName: "Submission Title",
      flex: autoFlex.get("submission.title"),
      minWidth: 100,
      sortable: true,
      renderCell: ({ row: { submission } }) => {
        return (
          <CellText variant="body2" noWrap>
            {submission?.title}
          </CellText>
        );
      },
    },

    {
      field: "notes",
      headerName: "Notes",
      flex: autoFlex.get("notes"),
      minWidth: 100,
      sortable: false,
      renderCell: ({ row }) => {
        const notes = row.submission?.notes;
        if (!notes) return "";
        return (
          <CellText variant="body2" noWrap>
            {notes}
          </CellText>
        );
      },
    },
    {
      field: "documents",
      headerName: "Attachment(s)",
      flex: autoFlex.get("documents"),
      minWidth: 60,
      sortable: false,
      renderCell: (params) => {
        const documents = params.row.submission?.documents || [];

        if (documents.length === 0) return "";
        const mappedDocuments = documents
          .map((doc) => doc.document.title)
          .join(", ");
        return (
          <Tooltip title={mappedDocuments}>
            <CellText variant="body2">{mappedDocuments}</CellText>
          </Tooltip>
        );
      },
    },
    {
      field: "reviewers",
      headerName: "Reviewer(s)",
      flex: autoFlex.get("reviewers"),
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        const reviewers = params.row.submission?.reviewers || [];
        const reviewMode = params.row.submission?.reviewMode;

        if (reviewers.length === 0) {
          return null;
        }

        return (
          <SubmissionReviewerCell
            reviewers={reviewers}
            reviewMode={reviewMode}
            isStaff={isStaff}
            type="reviewer"
          />
        );
      },
    },
    {
      field: "signer",
      headerName: "Signer",
      flex: autoFlex.get("signer"),
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        const reviewers = params.row.submission?.reviewers || [];
        const reviewMode = params.row.submission?.reviewMode;
        const signers = reviewers.filter((r) => r.isSigner);
        const signatureType = params.row.submission?.signatureType;

        // Handle no signature required cases
        if (signatureType === SignatureType.None || signers.length === 0) {
          return (
            <CellText variant="body2" noWrap>
              No Signature Required
            </CellText>
          );
        }

        return (
          <SubmissionReviewerCell
            reviewers={reviewers}
            reviewMode={reviewMode}
            isStaff={isStaff}
            type="signer"
          />
        );
      },
    },
    {
      field: "staffAssignee",
      headerName: "Encamp Assignee",
      flex: autoFlex.get("staffAssignee"),
      minWidth: 90,
      renderCell: ({ row }) => {
        if (!row?.submission?.staffAssignee) {
          return null;
        }
        const person = row.submission.staffAssignee.person;
        if (!person) {
          return (
            <CellText variant="body2" noWrap>
              {row.submission.staffAssignee.email}
            </CellText>
          );
        }
        const cellText =
          `${person.first || ""} ${person.last || ""}`.trim() ||
          row.submission.staffAssignee.email;
        return (
          <CellText variant="body2" noWrap>
            {cellText}
          </CellText>
        );
      },
    },
    {
      field: "submission.status",
      headerName: "Status",
      flex: autoFlex.get("submission.status"),
      minWidth: 120,
      sortable: true,
      renderCell({ row: { submission } }) {
        return (
          <CellText variant="body2" noWrap>
            {getStatusLabel(submission?.status ?? SubmissionStatus.NotStarted)}
          </CellText>
        );
      },
    },
    {
      field: "waitingOn",
      headerName: "Waiting On",
      flex: autoFlex.get("waitingOn"),
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        const waitingOn = params.row.waitingOn || [];

        if (waitingOn.length === 0) {
          return null;
        }

        return (
          <Stack
            spacing={0}
            style={{
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
              height: "100%",
              width: "100%",
              alignItems: "start",
              overflow: "hidden",
            }}
          >
            {waitingOn.map((user) => {
              const displayName =
                user.person?.first || user.person?.last
                  ? `${user.person.first || ""} ${
                      user.person.last || ""
                    }`.trim()
                  : user.email;

              return (
                <Typography
                  key={user.id || user.email}
                  variant="body2"
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Tooltip title={user.email}>
                    <span>{displayName}</span>
                  </Tooltip>
                </Typography>
              );
            })}
          </Stack>
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      sortable: false,
      align: "right",
      getActions: ({ row }) => {
        const actions: any[] = [];

        if (row.submission?.status === SubmissionStatus.NotStarted) {
          actions.push(
            <Tooltip title="Create Submission" key="createSubmission">
              <GridActionsCellItem
                onClick={() => handleRowClick(row)}
                label="Create Submission"
                icon={<Add />}
              />
            </Tooltip>
          );
        }

        return actions;
      },
    },
  ];

  const {
    omnisearch,
    setOmnisearch,
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
    columns: omnisearchColumns,
  } = useOmnisearchDatagrid({
    persistenceKey: omnisearchPersistenceKey,
    isURLDriven: true,
    columns,
    initialSortModel: [
      { field: "dueDate", sort: "asc" },
      { field: "title", sort: "asc" },
    ],
    initialPageSize: 50,
    initialFilters: INITIAL_FILTERS,
  });

  const handleReadyForActionChange = useCallback(
    (checked: boolean) => {
      if (checked && currentUserEmail) {
        editOmnisearchKeyValues([["readyForAction", currentUserEmail]]);
      } else {
        removeOmnisearchKeyValue("readyForAction");
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue, currentUserEmail]
  );

  // Status filter state - derived from omnisearch hook state
  const [statusOpen, setStatusOpen] = useState(false);
  const statusFilterValue = extractOmnisearchValue(
    omnisearch,
    "submissionStatus"
  );
  const selectedStatuses = useMemo(() => {
    // No filter = all statuses selected (show all)
    if (statusFilterValue === undefined || statusFilterValue === "") {
      return [...ALL_STATUSES];
    }
    return statusFilterValue.split("|").filter((s) => s.length > 0);
  }, [statusFilterValue]);

  // Status filter handler for individual status selections
  const handleStatusesChange = useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const raw = e.target.value as string[];
      // Filter out "all" - it's handled by onClick
      const values = raw.filter((v) => v !== "all");

      if (values.length === 0) {
        // Can't deselect all - revert to default
        editOmnisearchKeyValues([
          ["submissionStatus", DEFAULT_STATUSES.join("|")],
        ]);
      } else if (values.length === ALL_STATUSES.length) {
        // All selected = remove filter to show all
        removeOmnisearchKeyValue("submissionStatus");
      } else {
        editOmnisearchKeyValues([["submissionStatus", values.join("|")]]);
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  // Toggle all statuses handler
  const handleToggleAllStatuses = useCallback(() => {
    const isAllSelected = selectedStatuses.length === ALL_STATUSES.length;
    if (isAllSelected) {
      // Toggle from all → default (In Progress + Upcoming)
      editOmnisearchKeyValues([
        ["submissionStatus", DEFAULT_STATUSES.join("|")],
      ]);
    } else {
      // Select all
      removeOmnisearchKeyValue("submissionStatus");
    }
  }, [
    selectedStatuses.length,
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
  ]);

  const readyForAction = !!extractOmnisearchValue(omnisearch, "readyForAction");

  const handleViewUpcoming = useCallback(() => {
    editOmnisearchKeyValues([
      ["submissionStatus", SubmissionStatus.NotStarted],
    ]);
  }, [editOmnisearchKeyValues]);

  const clearOmnisearch = useCallback(() => {
    setOmnisearch("");
  }, [setOmnisearch]);

  // Derive timeFilter equivalent for empty state component
  const timeFilterForEmptyState = useMemo(() => {
    if (selectedStatuses.length === 1) {
      if (selectedStatuses[0] === SubmissionStatus.NotStarted)
        return "upcoming";
      if (
        selectedStatuses[0] === SubmissionStatus.FilingComplete ||
        selectedStatuses[0] === SubmissionStatus.NotReporting
      )
        return "completed";
    }
    // Check if it's the "in-progress" group
    const inProgressStatuses = [
      SubmissionStatus.InProgress,
      SubmissionStatus.EncampInitialReview,
      SubmissionStatus.InReview,
      SubmissionStatus.ProcessingSubmission,
    ];
    if (
      selectedStatuses.length > 0 &&
      selectedStatuses.every((s) =>
        inProgressStatuses.includes(s as SubmissionStatus)
      )
    ) {
      return "in-progress";
    }
    return "";
  }, [selectedStatuses]);

  // State filter state - derived from omnisearch hook state
  // Default: all states selected. Deselect all = no states (no results)
  const [stateOpen, setStateOpen] = useState(false);
  const statesFilterValue = extractOmnisearchValue(omnisearch, "states");

  const selectedStates = useMemo(() => {
    // No filter = all states selected
    if (statesFilterValue === undefined || statesFilterValue === "") {
      return [...US_STATES];
    }
    // "none" marker = no states selected
    if (statesFilterValue === "none") {
      return [];
    }
    return statesFilterValue.split("|").filter((s) => s.length > 0);
  }, [statesFilterValue]);

  const handleStatesChange = useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const raw = e.target.value as string[];
      const values = raw.filter((v) => v !== "select-all");

      if (values.length === 0) {
        // None selected = set "none" marker
        editOmnisearchKeyValues([["states", "none"]]);
      } else if (values.length === US_STATES.length) {
        // All selected = remove filter
        removeOmnisearchKeyValue("states");
      } else {
        // Specific states selected = apply filter
        editOmnisearchKeyValues([["states", values.join("|")]]);
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  const handleToggleAllStates = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (selectedStates.length === US_STATES.length) {
        // All selected -> deselect all
        editOmnisearchKeyValues([["states", "none"]]);
      } else {
        // Not all selected -> select all
        removeOmnisearchKeyValue("states");
      }
    },
    [selectedStates.length, removeOmnisearchKeyValue, editOmnisearchKeyValues]
  );

  // Date range filter state - derived from omnisearch hook state
  const dueDateValue = extractOmnisearchValue(omnisearch, "dueDate");
  const [dueDateStart, dueDateEnd] = useMemo(() => {
    // No filter in URL = show empty (user cleared it)
    if (!dueDateValue) {
      return [null, null];
    }
    const parts = dueDateValue.split("..");
    const start = parts[0] ? DateTime.fromISO(parts[0]) : null;
    const end = parts[1] ? DateTime.fromISO(parts[1]) : null;
    return [start, end];
  }, [dueDateValue]);

  const handleDueDateRangeChange = useCallback(
    (newValue: DateRange<DateTime>) => {
      const [start, end] = newValue;
      if (!start && !end) {
        removeOmnisearchKeyValue("dueDate");
      } else {
        const startStr = start?.toISODate() ?? "";
        const endStr = end?.toISODate() ?? "";
        editOmnisearchKeyValues([["dueDate", `${startStr}..${endStr}`]]);
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  // Clear bulk selections when filtering changes
  useEffect(() => {
    setSelectedRows([]);
    setSelectedIds([]);
  }, [omnisearch]);

  // Simple static title - filter context is shown in the Status dropdown
  const titleFilter = "Submissions";

  // Custom empty state overlay for the grid
  const CustomNoRowsOverlay = useCallback(
    () => (
      <SubmissionsEmptyState
        timeFilter={timeFilterForEmptyState}
        readyForAction={readyForAction}
        isStaff={isStaff}
        onCreateSubmission={handleAddSubmission}
        onViewUpcoming={handleViewUpcoming}
        omnisearch={omnisearch}
        onClearSearch={clearOmnisearch}
      />
    ),
    [
      timeFilterForEmptyState,
      readyForAction,
      isStaff,
      handleAddSubmission,
      handleViewUpcoming,
      omnisearch,
      clearOmnisearch,
    ]
  );

  const filterGroups: FilterGroup[] = useMemo(
    () => [
      {
        name: "Submission Info",
        fields: ["submission.title", "submissionType", "dueDate", "documents"],
      },
      {
        name: "Location",
        fields: ["facilityId"],
      },
      {
        name: "Assignment",
        fields: ["reviewers", "signer", "staffAssignee", "waitingOn"],
      },
    ],
    []
  );

  return (
    <Stack sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack
        id="submission-filter-header"
        paddingX={3}
        paddingY={2}
        sx={{
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderBottomColor: "divider",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Stack direction="row" gap={2} alignItems="flex-start">
            <FormControl variant="standard" size="small" sx={{ width: 220 }}>
              <InputLabel id="status-filter-label" shrink>
                Status
              </InputLabel>
              <Select
                labelId="status-filter-label"
                open={statusOpen}
                onOpen={() => setStatusOpen(true)}
                onClose={() => setStatusOpen(false)}
                multiple
                value={selectedStatuses}
                onChange={handleStatusesChange}
                label="Status"
                variant="standard"
                sx={{
                  "& .MuiSelect-select": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
                renderValue={(selected) => {
                  if (selected.length === ALL_STATUSES.length) {
                    return "📄 All";
                  }
                  if (selected.length <= 2) {
                    return (selected as string[])
                      .map(
                        (s) => STATUS_FILTER_OPTIONS[s as SubmissionStatus] || s
                      )
                      .join(", ");
                  }
                  return `${selected.length} selected`;
                }}
                MenuProps={{
                  MenuListProps: {
                    autoFocusItem:
                      selectedStatuses.length === ALL_STATUSES.length
                        ? false
                        : true,
                  },
                }}
              >
                <MenuItem value="all" onClick={handleToggleAllStatuses}>
                  <Checkbox
                    checked={selectedStatuses.length === ALL_STATUSES.length}
                  />
                  📄 All
                </MenuItem>
                {Object.entries(STATUS_FILTER_OPTIONS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    <Checkbox checked={selectedStatuses.includes(key)} />
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* State Filter */}
            <FormControl variant="standard" size="small" sx={{ width: 150 }}>
              <InputLabel id="state-filter-label" shrink>
                State
              </InputLabel>
              <Select
                labelId="state-filter-label"
                open={stateOpen}
                onOpen={() => setStateOpen(true)}
                onClose={() => setStateOpen(false)}
                multiple
                value={selectedStates}
                onChange={handleStatesChange}
                label="State"
                variant="standard"
                sx={{
                  "& .MuiSelect-select": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return "None";
                  }
                  if (selected.length === US_STATES.length) {
                    return `All (${selected.length})`;
                  }
                  if (selected.length <= 3) {
                    return (selected as string[]).join(", ");
                  }
                  return `${selected.length} selected`;
                }}
                MenuProps={{
                  MenuListProps: {
                    autoFocusItem:
                      selectedStates.length === US_STATES.length ? false : true,
                  },
                }}
              >
                <MenuItem value="select-all" onClick={handleToggleAllStates}>
                  <Checkbox
                    checked={selectedStates.length === US_STATES.length}
                    indeterminate={
                      selectedStates.length > 0 &&
                      selectedStates.length < US_STATES.length
                    }
                  />
                  Select All
                </MenuItem>
                {US_STATES.map((state) => (
                  <MenuItem key={state} value={state}>
                    <Checkbox checked={selectedStates.includes(state)} />
                    {state}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Due Date Range Filter */}
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DateRangePicker
                value={[dueDateStart, dueDateEnd]}
                onChange={handleDueDateRangeChange}
                localeText={{ start: "Due Date Start", end: "Due Date End" }}
                slotProps={{
                  shortcuts: {
                    items: datePickerShortcuts,
                  },
                  textField: {
                    variant: "standard",
                    size: "small",
                    sx: { width: 130 },
                    InputLabelProps: { shrink: true },
                  },
                }}
                slots={{
                  field: MultiInputDateRangeField,
                }}
              />
            </LocalizationProvider>

            <FormControlLabel
              control={
                <Switch
                  checked={readyForAction}
                  onChange={(e) => handleReadyForActionChange(e.target.checked)}
                  color="primary"
                />
              }
              label="Ready for next action"
            />
          </Stack>
          {isStaff && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddSubmission}
              color="secondary"
            >
              Create Submission
            </Button>
          )}
        </Stack>
      </Stack>
      <Stack
        id="submission-list"
        p={2}
        sx={{
          background: theme.palette.grey50.main,
          minHeight: "calc(100vh - 200px)",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            flexDirection: "column",
            position: "relative",
            pb: 3,
          }}
        >
          <Box sx={{ p: 3 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={1}
            >
              <Box>
                <Typography
                  variant="h5"
                  component="h5"
                  color="text.primary"
                  pb={1}
                >
                  {titleFilter}
                </Typography>
                <Typography fontSize={12} component="p">
                  General waste reporting submissions. Use the search and
                  filters to find specific submissions.
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box>
            <OmnisearchDataGrid
              slots={{ noRowsOverlay: CustomNoRowsOverlay }}
              hideEmptyOverlayWhileLoading
              persistenceKey={omnisearchPersistenceKey}
              isURLDriven
              initialFilters={INITIAL_FILTERS}
              columns={omnisearchColumns}
              dataQuery={SUBMISSION_VIRTUAL_EVENTS_QUERY}
              defaultSearch={`tenantId:${tenantId} status:ACTIVE|OVERDUE`}
              getItems={(data) => data.submissionVirtualEvents.items}
              getCount={(data) => data.submissionVirtualEvents.count ?? 0}
              checkboxSelection={isStaff}
              selectedIds={isStaff ? selectedIds : undefined}
              onSelectedRowsChanged={
                isStaff ? (rows) => setSelectedRows(rows) : undefined
              }
              onSelectedIdsChanged={
                isStaff ? (ids) => setSelectedIds(ids as string[]) : undefined
              }
              onRowClick={({ row }) => handleRowClick(row)}
              commandButtons={
                isStaff
                  ? [
                      <SubmissionBulkActionsMenu
                        key="bulk-actions"
                        submissions={selectedRows}
                        onClearSelections={() => {
                          setSelectedRows([]);
                          setSelectedIds([]);
                        }}
                      />,
                    ]
                  : []
              }
              initialPageSize={50}
              rowHeight={"auto"}
              columnVisibilityModel={{
                notes: false,
                waitingOn: false,
                ...(isMobile && {
                  facilities: false,
                  tags: false,
                }),
              }}
              additionalFilterColumns={[
                {
                  header: "Facility",
                  key: "facilityId",
                  filterKeyType: "multipleFacility",
                },
                {
                  header: "Attachment Name",
                  key: "documents",
                  filterKeyType: "text",
                },
                {
                  header: "Submission Type",
                  key: "submissionType",
                  filterKeyType: "text",
                },
                {
                  header: "Due Date",
                  key: "dueDate",
                  filterKeyType: "date",
                  dateRangeShortcuts: true,
                },
                {
                  header: "Reviewer",
                  key: "reviewers",
                  filterKeyType: "text",
                },
                {
                  header: "Signer",
                  key: "signer",
                  filterKeyType: "text",
                },
                {
                  header: "Encamp Assignee",
                  key: "staffAssignee",
                  filterKeyType: "text",
                },
                {
                  header: "Waiting On",
                  key: "waitingOn",
                  filterKeyType: "multipleUser",
                },
              ]}
              excludeFilterColumns={[
                "submissionType",
                "dueDate",
                "status",
                "documents",
                "reviewers",
                "signer",
                "staffAssignee",
                "facilityName",
                "title",
                "facilityId",
                "deadline",
                "facilities",
                "submission.status",
                "waitingOn",
              ]}
              filterGroups={filterGroups}
              tenantId={tenantId}
            />
          </Box>
        </Paper>
      </Stack>
    </Stack>
  );
}
